/**
 * Procurement Phase 6C — Finance Writers (canonical)
 * ──────────────────────────────────────────────────────────────────────
 * Sole sanctioned mutation surface for `PurchaseOrder.finance.*` (Model A,
 * VendorPOContext). Companion to `procurementWriters.ts` — same discipline,
 * same audit pattern, same `setPurchaseOrders` ownership.
 *
 * All writers:
 *   • Require a non-empty `actor`.
 *   • Are idempotent (no-op when the desired state already holds; no audit).
 *   • Append exactly one `ProcurementAuditEvent` on every successful change.
 *   • Reject (throw) on disallowed transitions — never coerce.
 *
 * Re-exported via `useProcurementWriters()` (same module surface).
 */
import { useCallback } from "react";
import {
  useVendorPO,
  type PurchaseOrder,
  type POPaymentState,
  type POCloseState,
  type ProcurementAuditEvent,
} from "@/data/VendorPOContext";
import { createProcurementAuditEvent } from "./procurementWriters";

/* ── Allowed payment-state transitions (locked by Phase 6C-0 §3.2) ── */
const ALLOWED_PAYMENT_TRANSITIONS: Record<POPaymentState, POPaymentState[]> = {
  none:     ["pending"],                       // strict: no none→partial / none→paid (refinement 2)
  pending:  ["partial", "paid", "disputed"],
  partial:  ["paid", "disputed"],
  paid:     ["disputed"],
  disputed: ["paid"],
};

const ALLOWED_CLOSE_TRANSITIONS: Record<POCloseState, POCloseState[]> = {
  open:           ["ready_to_close"],
  ready_to_close: ["closed", "open"],
  closed:         [],                          // 6D will add reopen
};

function appendAudit(po: PurchaseOrder, evt: ProcurementAuditEvent): PurchaseOrder {
  return { ...po, auditEvents: [...(po.auditEvents ?? []), evt] };
}

function getCurrentPaymentState(po: PurchaseOrder): POPaymentState {
  return po.finance?.paymentState ?? "none";
}

function getCurrentCloseState(po: PurchaseOrder): POCloseState {
  return po.finance?.closeState ?? "open";
}

export interface FinanceWriterMetadata {
  /** Bridge attribution. When present, marks this write as originating from
   *  the OrderingPacket → canonical bridge. Surfaced in audit metadata. */
  source?: "ordering_packet" | "direct";
  /** Bridge-only attribution flag explaining a multi-step sequence
   *  (e.g. `invoice_paid_autostep`). Surfaced in audit metadata. */
  bridgeReason?: string;
  /** Free-form additional metadata to thread through to the audit event. */
  [key: string]: unknown;
}

export function useFinanceWriters() {
  const { setPurchaseOrders } = useVendorPO();

  /* 1. Record an invoice reference (append-only) ────────── */
  const recordInvoiceReference = useCallback(
    (input: { poId: string; invoiceRef: string; actor: string; metadata?: FinanceWriterMetadata }) => {
      if (!input.actor?.trim()) throw new Error("recordInvoiceReference: actor required");
      if (!input.invoiceRef?.trim()) throw new Error("recordInvoiceReference: invoiceRef required");
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== input.poId) return po;
          const refs = po.finance?.invoiceRefs ?? [];
          if (refs.includes(input.invoiceRef)) return po; // idempotent
          const nextRefs = [...refs, input.invoiceRef];
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: po.poId,
            action: "po.invoice.recorded",
            actor: input.actor,
            metadata: { invoiceRef: input.invoiceRef, ...input.metadata },
          });
          return appendAudit(
            {
              ...po,
              finance: {
                ...(po.finance ?? {}),
                invoiceRefs: nextRefs,
                invoicesCount: nextRefs.length,
              },
            },
            evt,
          );
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 2. Update payment state (validated transition matrix) ── */
  const updatePaymentState = useCallback(
    (input: { poId: string; next: POPaymentState; reason?: string; actor: string; metadata?: FinanceWriterMetadata }) => {
      if (!input.actor?.trim()) throw new Error("updatePaymentState: actor required");
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== input.poId) return po;
          const current = getCurrentPaymentState(po);
          if (current === input.next) return po; // idempotent
          const allowed = ALLOWED_PAYMENT_TRANSITIONS[current] ?? [];
          if (!allowed.includes(input.next)) {
            throw new Error(
              `updatePaymentState: illegal transition ${current} → ${input.next} for ${po.poId}`,
            );
          }
          // Reason rules
          const requiresReason =
            input.next === "disputed" || current === "disputed";
          if (requiresReason && !(input.reason && input.reason.trim().length >= 10)) {
            throw new Error(
              `updatePaymentState: reason ≥10 chars required for ${current} → ${input.next}`,
            );
          }
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: po.poId,
            action: "po.payment.state_changed",
            actor: input.actor,
            ...(input.reason ? { reason: input.reason } : {}),
            metadata: { from: current, to: input.next, ...input.metadata },
          });
          return appendAudit(
            { ...po, finance: { ...(po.finance ?? {}), paymentState: input.next } },
            evt,
          );
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 3. Record dispute state (atomic open/close) ─────────── */
  const recordDisputeState = useCallback(
    (input: {
      poId: string;
      disputed: boolean;
      reason: string;
      alsoSetPaymentState?: boolean;
      actor: string;
      metadata?: FinanceWriterMetadata;
    }) => {
      if (!input.actor?.trim()) throw new Error("recordDisputeState: actor required");
      if (!input.reason || input.reason.trim().length < 10) {
        throw new Error("recordDisputeState: reason ≥10 chars required");
      }
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== input.poId) return po;
          const currentDisputed = po.finance?.disputed === true;
          if (currentDisputed === input.disputed && !input.alsoSetPaymentState) return po;
          const finance = { ...(po.finance ?? {}) };
          finance.disputed = input.disputed;
          finance.disputeReason = input.reason.trim();
          if (input.alsoSetPaymentState) {
            // Open: flip to disputed. Close: revert disputed → paid.
            if (input.disputed) finance.paymentState = "disputed";
            else if (finance.paymentState === "disputed") finance.paymentState = "paid";
          }
          const notes = finance.financialNotes ?? [];
          finance.financialNotes = [
            ...notes,
            `[${input.disputed ? "DISPUTE_OPEN" : "DISPUTE_CLOSED"}] ${input.reason.trim()}`,
          ];
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: po.poId,
            action: input.disputed ? "po.dispute.opened" : "po.dispute.closed",
            actor: input.actor,
            reason: input.reason.trim(),
            metadata: { alsoSetPaymentState: input.alsoSetPaymentState === true, ...input.metadata },
          });
          return appendAudit({ ...po, finance }, evt);
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 4. Update finance close state (gated) ───────────────── */
  const updateCloseState = useCallback(
    (input: { poId: string; next: POCloseState; reason?: string; actor: string; metadata?: FinanceWriterMetadata }) => {
      if (!input.actor?.trim()) throw new Error("updateCloseState: actor required");
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== input.poId) return po;
          const current = getCurrentCloseState(po);
          if (current === input.next) return po; // idempotent
          const allowed = ALLOWED_CLOSE_TRANSITIONS[current] ?? [];
          if (!allowed.includes(input.next)) {
            throw new Error(
              `updateCloseState: illegal transition ${current} → ${input.next} for ${po.poId}`,
            );
          }
          if (input.next === "closed") {
            const paymentState = po.finance?.paymentState ?? "none";
            const disputed = po.finance?.disputed === true;
            const ok = paymentState === "paid" || disputed;
            if (!ok) {
              throw new Error(
                `updateCloseState: cannot close ${po.poId} unless paymentState=paid or disputed=true`,
              );
            }
            if (!input.reason || input.reason.trim().length < 10) {
              throw new Error("updateCloseState: reason ≥10 chars required for close");
            }
          }
          if (input.next === "open" && current === "ready_to_close") {
            if (!input.reason || input.reason.trim().length < 10) {
              throw new Error("updateCloseState: reason ≥10 chars required to reopen ready_to_close");
            }
          }
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: po.poId,
            action: "po.close.state_changed",
            actor: input.actor,
            ...(input.reason ? { reason: input.reason } : {}),
            metadata: { from: current, to: input.next, ...input.metadata },
          });
          return appendAudit(
            { ...po, finance: { ...(po.finance ?? {}), closeState: input.next } },
            evt,
          );
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 5. Append-only finance metadata patch ───────────────── */
  const updatePurchaseOrderFinanceMetadata = useCallback(
    (input: {
      poId: string;
      patch: { receiptAccountingRefs?: string[]; financialNotes?: string[] };
      actor: string;
      metadata?: FinanceWriterMetadata;
    }) => {
      if (!input.actor?.trim()) throw new Error("updatePurchaseOrderFinanceMetadata: actor required");
      // Reject privileged keys
      const forbidden = ["paymentState", "disputed", "closeState", "invoiceRefs"] as const;
      for (const k of forbidden) {
        if (Object.prototype.hasOwnProperty.call(input.patch as Record<string, unknown>, k)) {
          throw new Error(
            `updatePurchaseOrderFinanceMetadata: '${k}' has a dedicated writer; reject patch`,
          );
        }
      }
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== input.poId) return po;
          const finance = { ...(po.finance ?? {}) };
          let mutated = false;
          if (input.patch.receiptAccountingRefs?.length) {
            const set = new Set(finance.receiptAccountingRefs ?? []);
            const before = set.size;
            input.patch.receiptAccountingRefs.forEach((r) => set.add(r));
            if (set.size !== before) {
              finance.receiptAccountingRefs = [...set];
              mutated = true;
            }
          }
          if (input.patch.financialNotes?.length) {
            finance.financialNotes = [...(finance.financialNotes ?? []), ...input.patch.financialNotes];
            mutated = true;
          }
          if (!mutated) return po; // idempotent
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: po.poId,
            action: "po.finance.metadata_appended",
            actor: input.actor,
            metadata: { ...input.metadata },
          });
          return appendAudit({ ...po, finance }, evt);
        }),
      );
    },
    [setPurchaseOrders],
  );

  return {
    recordInvoiceReference,
    updatePaymentState,
    recordDisputeState,
    updateCloseState,
    updatePurchaseOrderFinanceMetadata,
  };
}

export type FinanceWritersAPI = ReturnType<typeof useFinanceWriters>;
